<template>
  <div>
    <AvsTabs v-model="selectedTab">
      <template #tabs>
        <v-tab>
          <v-icon left> mdi-badge-account-horizontal-outline </v-icon>
          {{ $t("commons.informations") }}
        </v-tab>
        <v-tab>
          <v-icon left> mdi-lock-reset </v-icon>
          {{ $t("commons.pwd") }}
        </v-tab>
      </template>

      <template #items>
        <v-tab-item>
          <div class="subtitle-1 text--disabled">
            {{ $t("views.personal_settings.subtitle") }}
          </div>

          <div class="d-flex flex-wrap row-gap-5 mt-5">
            <div class="col-12 col-md-6">
              <v-form
                ref="profile-form"
                v-model="isFormValid['profile-form']"
              >
                <v-text-field
                  clearable
                  :label="$t('commons.first_name')"
                  :rules="rules.profile.first_name"
                  v-model="profile.first_name"
                ></v-text-field>

                <v-text-field
                  clearable
                  :label="$t('commons.last_name')"
                  :rules="rules.profile.last_name"
                  v-model="profile.last_name"
                ></v-text-field>
                <v-text-field
                  :rules="rules.profile.email"
                  :label="$t('commons.email')"
                  disabled
                  v-model="profile.email"
                ></v-text-field>

                <div class="d-flex flex-column align-end">
                  <avs-saveButton
                    :disabled="!isFormValid['profile-form'] || hasChangedProfile"
                    :loading="changedForm"
                    @click="save('profile-form')"
                  />
                </div>
              </v-form>
              <hr class="mt-5" />
              <v-form
                ref="signature-form"
                v-model="isFormValid['signature-form']"
              >
                <v-col
                  cols="12"
                  md="6"
                  class="primary--text font-weight-bold"
                >
                  {{ $t("Signature") }}
                  <avs-button
                    color="blue white--text"
                    iconI="mdi-upload"
                    class="mb-5"
                    @click="handleUpload()"
                    :spantext="
                      $t('actions.upload', { entity: $tc('commons.picture', 1).toLowerCase() })
                    "
                    ><template v-slot:name>{{
                      $t("actions.upload", { entity: $tc("commons.signature", 1).toLowerCase() })
                    }}</template>
                  </avs-button>
                  <v-row>
                    <v-col
                      cols="12"
                      sm="6"
                      md="6"
                      v-if="signaturePicture"
                    >
                      <v-card class="mx-auto overflow-hidden">
                        <div
                          @mouseleave="signaturePicture.hover = false"
                          @mouseenter="signaturePicture.hover = true"
                          :style="{ position: 'relative' }"
                        >
                          <v-fade-transition>
                            <v-overlay
                              v-show="signaturePicture.hover"
                              absolute
                              color="primary"
                              z-index="99"
                            >
                              <PicturePreview v-model="signaturePicture.file_url">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    text
                                    v-on="on"
                                    :attrs="attrs"
                                  >
                                    <v-icon left>mdi-eye</v-icon>
                                    {{ $t("commons.preview") }}
                                  </v-btn>
                                </template>
                              </PicturePreview>
                            </v-overlay>
                          </v-fade-transition>
                          <v-img
                            :src="signaturePicture.file_url"
                            height="150"
                            width="250"
                          ></v-img>
                        </div>
                        <div class="justify-center">
                          <avs-deleteButton
                            @click.stop="handleOpenDeleteSignatureDialog(signaturePicture)"
                            :spantext="
                              $t('actions.delete', {
                                entity: $tc('commons.picture', 1).toLowerCase()
                              })
                            "
                          />
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                  <input
                    v-show="false"
                    type="file"
                    accept="image/*"
                    @change="handleChange('signature', $event)"
                    ref="uploadInput_signature"
                  />
                </v-col>

                <div class="d-flex flex-column align-end">
                  <avs-saveButton
                    :disabled="desactivated_sign"
                    :loading="changedForm"
                    @click="sign()"
                  />
                </div>
              </v-form>
            </div>

            <div class="col-12 col-md-6">
              <v-data-table
                :headers="headers"
                :items="itemsCompanies"
                hide-default-footer
                class="elevation-0"
              >
                <template v-slot:item.company_role="{ item }">
                  <avs-role :role="item.company_role" />
                </template>
                <template v-slot:item.actions="{ item }">
                  <avs-leaveButton
                    :disabled="isAdmin(item.company_role)"
                    @click.stop="handleOpenDeleteDialog(item)"
                    :spantext="
                      $t('actions.leave', { entity: $t('categories.company.name').toLowerCase() })
                    "
                  />
                </template>
              </v-data-table>
            </div>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="subtitle-1 text--disabled">
            {{ $t("views.personal_settings.changing_pwd_subtitle") }}
          </div>
          <div class="d-flex mt-10">
            <div class="col-12 col-md-6">
              <v-form
                ref="change-password-form"
                v-model="isFormValid['change-password-form']"
              >
                <v-text-field
                  clearable
                  :label="$t('views.personal_settings.current_pwd')"
                  :rules="rules.password.old"
                  v-model="password.old"
                  :type="showPassword.old ? 'text' : 'password'"
                  autocomplete="off"
                  :append-icon="showPassword.old ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showPassword.old = !showPassword.old"
                ></v-text-field>

                <v-text-field
                  clearable
                  :label="$t('views.personal_settings.new_pwd')"
                  :rules="rules.password.new"
                  v-model="password.new"
                  :type="showPassword.new ? 'text' : 'password'"
                  autocomplete="off"
                  :append-icon="showPassword.new ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showPassword.new = !showPassword.new"
                ></v-text-field>

                <v-text-field
                  clearable
                  :label="$t('views.personal_settings.repeat_pwd')"
                  :rules="rules.password.newRepeat"
                  v-model="password.newRepeat"
                  :type="showPassword.newRepeat ? 'text' : 'password'"
                  autocomplete="off"
                  :append-icon="showPassword.newRepeat ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showPassword.newRepeat = !showPassword.newRepeat"
                ></v-text-field>
                <div class="d-flex flex-column align-end">
                  <avs-saveButton
                    :disabled="!isFormValid['change-password-form']"
                    :loading="changedForm"
                    @click="save('change-password-form')"
                  />
                </div>
              </v-form>
            </div>
            <div class="col-12 col-md-1"></div>
            <div class="col-12 col-md-5">
              <v-form
                ref="change-mfa-form"
                v-model="isFormValid['change-mfa-form']"
              >
                <v-col class="col-12 pb-0 mb-0">
                  <div>
                    <h3>{{ $t("commons.activate_mfa") }}</h3>
                  </div>
                  <v-switch
                    inset
                    color="success"
                    v-model="mfaStatus"
                    @change="confirmMFAChange"
                    :label="mfaStatus ? 'Active' : 'Inactive'"
                  ></v-switch>
                </v-col>
              </v-form>
            </div>
          </div>
        </v-tab-item>

        <v-dialog
          v-model="dialog"
          max-width="500"
        >
          <v-card>
            <v-card-title class="text-h5"> {{ $t("commons.confirm") }} </v-card-title>
            <v-card-text>
              {{ mfaStatus ? $t("commons.activate_mfa") : $t("commons.disable_mfa") }}
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click="cancelChange"
                >Cancel</v-btn
              >
              <v-btn
                color="blue darken-1"
                text
                @click="toggleMFA"
                >Confirm</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </AvsTabs>
    <avs-deleteDialog
      v-model="deleteDialogIsOpen"
      @cancel="handleCancel"
      @delete="leaveCompany"
      :resource="$t('categories.company.name').toLowerCase()"
    />
    <avs-deleteDialog
      v-model="deleteSignDialogIsOpen"
      @cancel="handleCancel"
      @delete="deleteSignature"
      :resource="$t('categories.company.name').toLowerCase()"
    />
  </div>
</template>

<script>
import { AccountService } from "@/services"
import { initials } from "@/utils/string"
import { required, email, password, imageValidator } from "@/utils/validator"
import CompanyService from "@/services/CompanyService"
import { isAdmin } from "@/utils/middleware"

export default {
  components: {
    AvsTabs: () => import("@/components/generics/general/AvsTabs.vue"),
    PicturePreview: () => import("@/components/ri3/non-conformity/NonConformityPicturePreview.vue")
  },
  data() {
    return {
      desactivated_sign: true,
      deleteDialogIsOpen: false,
      deleteSignDialogIsOpen: false,
      signaturePicture: null,
      selectedTab: null,
      showPassword: {
        old: false,
        new: false,
        newRepeat: false
      },
      dialog: false,
      mfaStatus: false,
      originalStatus: false,
      profile: {
        email: "mark.trenton@ri3.com",
        first_name: "Mark",
        last_name: "Trenton"
      },
      password: {
        old: "",
        new: "",
        newRepeat: ""
      },
      isFormValid: {
        "change-password-form": true,
        "profile-form": true
      },
      rules: {
        profile: {
          first_name: [required],
          last_name: [required],
          email: [required, email]
        },
        password: {
          old: [required],
          new: [required, password],
          newRepeat: [
            required,
            password,
            v =>
              v === this.password.new || this.$t("views.personal_settings.verify_right_entered_pwd")
          ]
        }
      },
      changedForm: false,
      refProfile: {
        first_name: "",
        last_name: ""
      },
      headers: [
        {
          text: this.$t("categories.company.name"),
          align: "start",
          sortable: "true",
          value: "name"
        },
        { text: this.$t("commons.role"), align: "center", sortable: "true", value: "company_role" },
        { text: this.$t("commons.actions"), sortable: false, value: "actions", align: "right" }
      ],
      itemsCompanies: [],
      selectedItem: null
    }
  },
  computed: {
    hasChangedProfile() {
      return (
        this.profile.first_name == this.refProfile?.first_name &&
        this.profile.last_name == this.refProfile?.last_name
      )
    }
  },
  mounted() {
    this.getCompanyInfos()
    this.loadUserSignature()
    this.getMFAStatut()
  },
  created() {
    this.patchValues(this.$store.state.app.user)
  },
  methods: {
    initials,
    isAdmin,
    leaveCompany() {
      CompanyService.selfRemoveFromCompany(this, this.selectedItem.pk).then(r => {
        this.$store.commit("app/message", {
          msg: "User removed from the company",
          color: "success"
        })
        this.getCompanyInfos()
      })
      this.deleteDialogIsOpen = false
    },
    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },
    handleOpenDeleteSignatureDialog(item) {
      this.deleteSignDialogIsOpen = true
      this.selectedItem = item
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
      this.deleteSignDialogIsOpen = false
    },
    patchValues(v) {
      this.profile.email = v.email
      this.profile.first_name = v.first_name
      this.profile.last_name = v.last_name
      this.refProfile.first_name = v.first_name
      this.refProfile.last_name = v.last_name
    },
    resetValidation(formRef) {
      if (formRef && this.$refs[formRef]) this.$refs[formRef].resetValidation()
    },
    reset(formRef) {
      if (formRef && this.$refs[formRef]) {
        this.$refs[formRef].reset()
        this.resetValidation(formRef)
      }
    },
    handleChange(type, event) {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        if (imageValidator(file)) {
          let reader = new FileReader()
          reader.onload = () => {
            this[type + "Picture"] = {
              file_url: reader.result,
              label: "",
              hover: false,
              removed: false
            }
          }
          reader.readAsDataURL(file)
          this.desactivated_sign = false
        }
      })
    },
    handleUpload() {
      this.$refs.uploadInput_signature.click()
    },
    save(formRef) {
      if (this.changedForm) return
      this.$refs[formRef].validate()
      this.$nextTick(() => {
        if (this.isFormValid[formRef]) {
          switch (formRef) {
            case "change-password-form":
              this.submitChangePassword()
              break
            case "profile-form":
              this.submitProfile()
              break
          }
        }
      })
    },
    sign() {
      if (this.signaturePicture) {
        // Vérifiez la taille du fichier en octets (2 Mo = 2 * 1024 * 1024 octets)
        const maxFileSize = 2 * 1024 * 1024 // 2 Mo

        // Supposons que `this.signaturePicture` contienne un objet fichier avec un attribut `size`
        if (this.signaturePicture.size > maxFileSize) {
          this.$store.commit("app/message", {
            msg: "The file size exceeds the maximum limit of 2 MB.",
            color: "error"
          })
          return
        }
        // Préparez les données de l'image (supposons que vous voulez envoyer la première image)
        const imageData = this.signaturePicture.file_url
        // Appelez la fonction post avec l'ID du document et les données de l'image
        AccountService.postSignature(this, imageData)
          .then(r => {
            this.$store.commit("app/message", {
              msg: "Signature updated successfully",
              color: "success"
            })
            this.desactivated_sign = true
            this.loadUserSignature()
          })
          .catch(e => {
            if (e.response && e.response.status === 400) {
              this.$store.commit("app/message", {
                msg: "The file size exceeds the maximum limit of 2 MB.",
                color: "error"
              })
            } else {
              this.$store.commit("app/message", {
                msg: e.response,
                color: "error"
              })
            }
          })
      }
    },
    loadUserSignature() {
      AccountService.getSignatures().then(v => {
        if (v && v.signature_picture) {
          this.signaturePicture = {
            id: v.id,
            file_url: v.signature_picture,
            label: "",
            hover: false,
            removed: false
          }
        }
      })
    },
    deleteSignature() {
      if (this.signaturePicture?.id) {
        AccountService.selfRemoveSignatures()
          .then(() => {
            this.$store.commit("app/message", {
              msg: this.$t("messages.information.signature"),
              color: "success"
            })
            this.loadUserSignature()
          })
          .catch(error => {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.signature_delete"),
              color: "error"
            })
            console.error("Error deleting signature:", error)
          })
      } else {
        this.signaturePicture = null

        this.$store.commit("app/message", {
          msg: this.$t("messages.information.signature"),
          color: "success"
        })
        this.loadUserSignature()
      }

      this.deleteSignDialogIsOpen = false
    },
    submitChangePassword() {
      const formRef = "change-password-form"
      AccountService.changePassword(
        this,
        this.password.old,
        this.password.new,
        this.password.newRepeat
      )
        .then(() => {
          this.reset(formRef)
        })
        .finally(() => {
          this.changedForm = false
        })
    },
    submitProfile() {
      const $ = this
      AccountService.updateProfileInfo(
        $,
        $.profile.first_name,
        $.profile.last_name,
        $.profile.email
      )
        .then(() => {
          $.$nextTick(() => {
            $.patchValues($.$store.state.app.user)
          })
        })
        .finally(() => {
          $.changedForm = false
        })
    },
    getCompanyInfos() {
      const $ = this
      CompanyService.getAll()
        .then(results => {
          this.itemsCompanies = results
        })
        .finally(() => {
          $.changedForm = false
        })
    },
    getMFAStatut() {
      AccountService.getMFAStatus().then(response => {
        this.mfaStatus = response.data.is_active
        this.originalStatus = this.mfaStatus
      })
    },
    confirmMFAChange() {
      // Ouvrir le modal de confirmation lorsque le switch est modifié
      this.dialog = true
    },
    cancelChange() {
      // Si l'utilisateur annule, remettre la valeur originale du switch
      this.mfaStatus = this.originalStatus
      this.dialog = false
    },
    toggleMFA() {
      // Appel API pour activer/désactiver MFA
      AccountService.toggleMFA({ method: "email", is_active: this.mfaStatus })
        .then(() => {
          this.originalStatus = this.mfaStatus
          this.dialog = false
          this.$store.commit("app/success", "MFA status updated successfully")
        })
        .catch(() => {
          this.$store.commit("app/error", "Failed to update MFA status")
        })
    }
  }
}
</script>

<style>
.row-gap-5 {
  row-gap: 20px;
}
</style>
